import styled from "@emotion/styled";
import { useHeadsUpClick } from "@/components/heads-up/context/heads-up-context";
import { SmartTimelineRow } from "@/components/heads-up/cards/common/smart-timeline-row";
import { SmartCardSection } from "@/components/heads-up/cards/common/layout";
import { CollapsibleSectionHeader } from "@/components/heads-up/cards/common/collapsible-section-header";
import { useIncrementalReveal } from "@/components/heads-up/cards/common/use-incremental-reveal";
import type { FC } from "react";
import type { Maybe } from "@/domains/common/types";
import type { EventTimelineSmartCard } from "@/components/heads-up/heads-up.types";
import { SeeMoreButton } from "@/components/heads-up/cards/common/see-more-button";
import type { SmartNotePreviewInfo } from "@/components/heads-up/cards/common/smart-note-preview.types";

const EVENT_FALLBACK_TITLE = "Event";
const LINKED_NOTE_FALLBACK_TITLE = "Note";

type Props = {
  card: EventTimelineSmartCard;
  getNotePreviewInfo?: (noteId: string) => Maybe<SmartNotePreviewInfo>;
};

export const EventTimelineSmartCardRenderer: FC<Props> = ({ card, getNotePreviewInfo }) => {
  const clickHandlers = useHeadsUpClick();

  const sectionTitle = card.value.title;
  const sorted = [...card.value.items].sort(
    (a, b) => Date.parse(b.associated_datetime) - Date.parse(a.associated_datetime)
  );

  const { visibleItems, hasMore, showAll, reset } = useIncrementalReveal({
    cardId: card.id,
    items: sorted,
    initialCount: 4,
    step: 5,
  });

  return (
    <SmartCardSection>
      <CollapsibleSectionHeader title={sectionTitle} onCollapseChange={c => c && reset()}>
        <TimelineList>
          {visibleItems.map((item, index) => {
            const primary = item.primary_label ?? EVENT_FALLBACK_TITLE;
            const linkedId = item.linked_note_id ?? undefined;
            const linkedPrimaryLabel = item.linked_note_primary_label ?? undefined;
            const onClick = linkedId ? clickHandlers?.noteClick(linkedId) : undefined;
            const notePreviewInfo = linkedId ? getNotePreviewInfo?.(linkedId) : undefined;
            const noteTitle = linkedPrimaryLabel ?? LINKED_NOTE_FALLBACK_TITLE;

            return (
              <SmartTimelineRow
                key={item.event_smart_entity_id}
                noteId={linkedId}
                primary={primary}
                datetime={item.associated_datetime}
                onClick={onClick}
                notePreviewInfo={notePreviewInfo}
                noteTitle={noteTitle}
                showTopConnector={index !== 0}
                showBottomConnector={index !== sorted.length - 1}
              />
            );
          })}
          <SeeMoreButton hasMore={hasMore} onClick={showAll} />
        </TimelineList>
      </CollapsibleSectionHeader>
    </SmartCardSection>
  );
};

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2px;
`;
